// Types
// Components
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
// Utils
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
// Slices
import {
  availabilityRelationLoadingSelector,
  availabilityRelationMapSelector,
  availabilityRelationsSelector,
  checkFirstAvailabilityRelationExists,
  createEmptyAvailabilityRelation,
  deleteAvailabilityRelation,
  duplicateAvailabilityRelation,
  selectedAvailabilityRelationIdsSelector,
  setFirstAvailabilityRelation,
  setSecondAvailabilityRelation,
} from 'slices/availabilityRelation.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { matchString } from 'utils/stringUtils';
import type { TAvailabilityRelationType } from '@timeedit/types';
import {
  Button,
  EditableTable,
  Filter,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Styles
import './AvailabilityRelationList.scss';

interface IRowData {
  id: number;
  name: string;
}

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const AvailabilityRelationList = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const availabilityRelations: TAvailabilityRelationType[] = useAppSelector(
    availabilityRelationsSelector
  );
  const availabilityRelationMap: Record<number, TAvailabilityRelationType> =
    useAppSelector(availabilityRelationMapSelector);
  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const selectedIds: number[] = useAppSelector(
    selectedAvailabilityRelationIdsSelector
  );

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(selectedIds);
    }, 100);
  }, [selectedIds.join('-')]);

  const loading = useAppSelector(availabilityRelationLoadingSelector);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const filterData: IRowData[] = useMemo(() => {
    const dataSource = availabilityRelations.map((padding) => ({
      id: padding.id,
      name: padding.name || getText('new_availability_relation'),
    }));

    return dataSource.filter((pr) => {
      let matchedText = true;
      const { freeTextSearch } = filterSettings;
      if (freeTextSearch) {
        matchedText = matchString(pr.name, freeTextSearch);
      }
      return matchedText;
    });
  }, [availabilityRelations, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardPaddingChanges(true, () =>
      dispatch(createEmptyAvailabilityRelation())
    );
  }, []);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(
          duplicateAvailabilityRelation(availabilityRelationMap[record.id])
        )
      );
    },
    [availabilityRelationMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstAvailabilityRelationExist = dispatch(
        checkFirstAvailabilityRelationExists()
      );
      if (!firstAvailabilityRelationExist) {
        return dispatch(
          setFirstAvailabilityRelation(availabilityRelationMap[record.id])
        );
      }
      confirmDiscardPaddingChanges(false, () =>
        dispatch(
          setSecondAvailabilityRelation(availabilityRelationMap[record.id])
        )
      );
    },
    [availabilityRelationMap]
  );

  const onRowClick = useCallback(
    ({ id }: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(setFirstAvailabilityRelation(availabilityRelationMap[id]))
      );
    },
    [availabilityRelationMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () =>
        dispatch(deleteAvailabilityRelation(record.id))
      ),
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstAvailabilityRelationExists())),
    [dispatch]
  );
  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button size="small" type="primary" onClick={onCreate}>
          Create new
        </Button>
      </Space>
    ),
    [onCreate]
  );

  const title = useTableTitle<IRowData>({
    total: availabilityRelations.length,
    filteredAmount: filterData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="availability-relation-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={options}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(AvailabilityRelationList);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    ellipsis: true,
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
  },
];

const defaultFields: TEIFilterField[] = [];
const options = {};
